import { createContext, useContext, useMemo, useState } from 'react';
import type { StepperContextType, StepperDataBase, StepperProviderProps } from '../components/stepper-common.types';
type StepperHookReturn = {
  StepperProvider: ({
    children
  }: StepperProviderProps) => JSX.Element;
  useStepper: () => {
    getStepper: (id: string, totalSteps: number, initialStep?: number, onNext?: (fromStep: number, toStep: number) => void, onPrevious?: (fromStep: number, toStep: number) => void) => StepperContextType;
  };
};
const getDefaultStepperData = (totalSteps = 0, initialStep = 0, currentStep = 0): StepperDataBase => ({
  initialStep,
  totalSteps,
  currentStep,
  isFirstStep: currentStep === 0,
  isLastStep: currentStep === totalSteps - 1
});
export const createStepperHook = (): StepperHookReturn => {
  const StepperContext = createContext<{
    getStepper: (id: string, totalSteps: number, initialStep?: number, onNext?: (fromStep: number, toStep: number) => void, onPrevious?: (fromStep: number, toStep: number) => void) => StepperContextType;
  } | undefined>(undefined);
  const StepperProvider = ({
    children
  }: StepperProviderProps) => {
    const [stepperData, setStepperData] = useState<Record<string, StepperDataBase>>({});
    const contextValue = useMemo(() => {
      const getStepper = (id: string, totalSteps: number, initialStep = 0, onNext?: (fromStep: number, toStep: number) => void, onPrevious?: (fromStep: number, toStep: number) => void) => {
        const stepper = stepperData[id] ?? getDefaultStepperData(totalSteps, initialStep, initialStep);
        const nextStep = () => {
          setStepperData(prev => {
            const currentStepper = prev[id] || stepper;
            const newStep = currentStepper.currentStep < totalSteps - 1 ? currentStepper.currentStep + 1 : currentStepper.currentStep;
            onNext?.(currentStepper.currentStep, newStep);
            return {
              ...prev,
              [id]: {
                ...currentStepper,
                currentStep: newStep
              }
            };
          });
        };
        const prevStep = () => {
          setStepperData(prev => {
            const currentStepper = prev[id] || stepper;
            const newStep = currentStepper.currentStep > 0 ? currentStepper.currentStep - 1 : currentStepper.currentStep;
            onPrevious?.(currentStepper.currentStep, newStep);
            return {
              ...prev,
              [id]: {
                ...currentStepper,
                currentStep: newStep
              }
            };
          });
        };
        const goToStep = (step: number) => {
          setStepperData(prev => {
            const currentStepper = prev[id] || stepper;
            if (step >= 0 && step < currentStepper.totalSteps) {
              return {
                ...prev,
                [id]: {
                  ...currentStepper,
                  currentStep: step
                }
              };
            }
            return prev;
          });
        };
        const resetSteps = () => {
          setStepperData(prev => ({
            ...prev,
            [id]: getDefaultStepperData(prev[id]?.totalSteps || 0, 0, 0)
          }));
        };
        return {
          currentStep: stepper.currentStep,
          nextStep,
          prevStep,
          goToStep,
          resetSteps,
          isFirstStep: stepper.currentStep === 0,
          isLastStep: stepper.currentStep === stepper.totalSteps - 1
        };
      };
      return {
        getStepper
      };
    }, [stepperData]);
    return <StepperContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="StepperProvider" data-sentry-source-file="use-stepper-common.tsx">{children}</StepperContext.Provider>;
  };
  const useStepper = () => {
    const context = useContext(StepperContext);
    if (!context) {
      throw new Error('useStepper must be used within a StepperProvider');
    }
    return context;
  };
  return {
    StepperProvider,
    useStepper
  };
};